import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import OrderReceiptDialog from './OrderReceiptDialog';
import Logo from '../../components/Logo/Logo';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'closed':
      return 'success';
    case 'placed':
      return 'info';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const getDeliveryMethodColor = (method) => {
  switch (method?.toLowerCase()) {
    case 'delivery':
      return 'primary';
    case 'pickup':
      return 'secondary';
    case 'curbside':
      return 'warning';
    case 'dinein':
      return 'info';
    default:
      return 'default';
  }
};

const OLOCoreLookup = () => {
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authenticating } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  // Data state
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [selectedStore, setSelectedStore] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('all');
  const [orderStatus, setOrderStatus] = useState('all');
  const [hasCoupon, setHasCoupon] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sort state
  const [sortBy, setSortBy] = useState('time_placed');
  const [sortOrder, setSortOrder] = useState('desc');

  // Order detail drawer
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Fetch stores on mount
  useEffect(() => {
    if (firebaseUser) {
      fetchStores();
    }
  }, [firebaseUser]);

  // Fetch orders when filters change
  useEffect(() => {
    if (firebaseUser && startDate && endDate) {
      fetchOrders();
    }
  }, [
    firebaseUser,
    startDate,
    endDate,
    selectedStore,
    deliveryMethod,
    orderStatus,
    hasCoupon,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
  ]);

  const fetchStores = async () => {
    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get('/hd-api/olo-core/stores', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(response.data.stores || []);
    } catch (err) {
      console.error('Error fetching stores:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        page: page.toString(),
        limit: rowsPerPage.toString(),
        sortBy,
        sortOrder,
      });

      if (selectedStore !== 'all') params.append('storeNumber', selectedStore);
      if (deliveryMethod !== 'all')
        params.append('deliveryMethod', deliveryMethod);
      if (orderStatus !== 'all') params.append('orderStatus', orderStatus);
      if (hasCoupon !== 'all') params.append('hasCoupon', hasCoupon);
      if (searchQuery) params.append('searchQuery', searchQuery);

      const response = await axios.get(
        `/hd-api/olo-core/orders?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setOrders(response.data.orders || []);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (orderRef) => {
    try {
      setLoadingDetail(true);
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(`/hd-api/olo-core/order/${orderRef}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderDetail(response.data);
    } catch (err) {
      console.error('Error fetching order detail:', err);
      setOrderDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchOrders();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setDrawerOpen(true);
    fetchOrderDetail(order.orderRef);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setOrderDetail(null);
    setSelectedOrder(null);
  };

  const columns = [
    { id: 'order_ref', label: 'Order Ref', width: 120 },
    { id: 'time_placed', label: 'Time Placed', width: 150 },
    { id: 'time_wanted', label: 'Time Wanted', width: 150 },
    { id: 'store_number', label: 'Store', width: 80 },
    { id: 'customerName', label: 'Customer', width: 150, sortable: false },
    { id: 'deliveryMethod', label: 'Method', width: 100, sortable: false },
    { id: 'order_status', label: 'Status', width: 100 },
    { id: 'total', label: 'Total', width: 100, align: 'right' },
  ];

  if (authenticating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '20%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth='xl'>
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) =>
                  `4px solid ${theme.palette.secondary.main}`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) =>
                    alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FastfoodIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='h5'
                  sx={{
                    fontWeight: 600,
                    fontFamily: 'Lato',
                    color: 'text.primary',
                  }}
                >
                  OLO Core Orders
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  View and search orders from OLO Core
                </Typography>
              </Box>
              <Button
                variant='outlined'
                color='success'
                size='small'
                startIcon={<LocalOfferIcon />}
                onClick={() => navigate('/olo-core-lookup/coupons')}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Coupon Analytics
              </Button>
            </Box>
          </Paper>

          {/* Filters */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
              }}
            >
              {/* Date Range */}
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                placeholderText='Start Date'
                customInput={
                  <TextField
                    size='small'
                    label='Start Date'
                    sx={{ width: 150 }}
                    autoComplete='off'
                  />
                }
              />
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                placeholderText='End Date'
                customInput={
                  <TextField
                    size='small'
                    label='End Date'
                    sx={{ width: 150 }}
                    autoComplete='off'
                  />
                }
              />

              {/* Store Filter */}
              <FormControl size='small' sx={{ minWidth: 150 }}>
                <InputLabel>Store</InputLabel>
                <Select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  label='Store'
                >
                  <MenuItem value='all'>All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.storeNumber} value={store.storeNumber}>
                      {store.storeNumber} - {store.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Delivery Method Filter */}
              <FormControl size='small' sx={{ minWidth: 130 }}>
                <InputLabel>Method</InputLabel>
                <Select
                  value={deliveryMethod}
                  onChange={(e) => setDeliveryMethod(e.target.value)}
                  label='Method'
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='Pickup'>Pickup</MenuItem>
                  <MenuItem value='Delivery'>Delivery</MenuItem>
                  <MenuItem value='Curbside'>Curbside</MenuItem>
                  <MenuItem value='DineIn'>Dine In</MenuItem>
                </Select>
              </FormControl>

              {/* Order Status Filter */}
              <FormControl size='small' sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={orderStatus}
                  onChange={(e) => setOrderStatus(e.target.value)}
                  label='Status'
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='placed'>Placed</MenuItem>
                  <MenuItem value='closed'>Closed</MenuItem>
                  <MenuItem value='cancelled'>Cancelled</MenuItem>
                </Select>
              </FormControl>

              {/* Coupon Filter */}
              <FormControl size='small' sx={{ minWidth: 120 }}>
                <InputLabel>Coupon</InputLabel>
                <Select
                  value={hasCoupon}
                  onChange={(e) => setHasCoupon(e.target.value)}
                  label='Coupon'
                >
                  <MenuItem value='all'>All</MenuItem>
                  <MenuItem value='yes'>With Coupon</MenuItem>
                  <MenuItem value='no'>No Coupon</MenuItem>
                </Select>
              </FormControl>

              {/* Search */}
              <TextField
                size='small'
                placeholder='Search orders...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                sx={{ width: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={handleClearSearch}>
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant='contained'
                size='small'
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>

              <Tooltip title='Refresh data'>
                <IconButton onClick={fetchOrders} size='small'>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Orders Table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: '#fafafa',
                          width: column.width,
                          ...(column.align && { textAlign: column.align }),
                        }}
                      >
                        {column.sortable !== false ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        sx={{ textAlign: 'center', py: 4 }}
                      >
                        <Typography color='text.secondary'>
                          No orders found. Try adjusting your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => (
                      <TableRow
                        key={order.id}
                        hover
                        onClick={() => handleRowClick(order)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography
                            variant='body2'
                            sx={{ fontWeight: 500, color: 'primary.main' }}
                          >
                            {order.orderRef}
                          </Typography>
                        </TableCell>
                        <TableCell>{order.timePlaced}</TableCell>
                        <TableCell>{order.timeWanted}</TableCell>
                        <TableCell>{order.storeNumber}</TableCell>
                        <TableCell>
                          <Typography variant='body2' noWrap>
                            {order.customerName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.deliveryMethod || '-'}
                            size='small'
                            color={getDeliveryMethodColor(order.deliveryMethod)}
                            variant='outlined'
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.orderStatus || '-'}
                            size='small'
                            color={getStatusColor(order.orderStatus)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {formatCurrency(order.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      </Container>

      {/* Order Detail Drawer */}
      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              Order Details
            </Typography>
            <Box>
              {orderDetail && (
                <Tooltip title='View Receipt'>
                  <IconButton
                    onClick={() => setReceiptDialogOpen(true)}
                    sx={{ mr: 0.5 }}
                  >
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orderDetail ? (
            <Box>
              {/* Order Info */}
              <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <ReceiptIcon color='primary' fontSize='small' />
                  <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                    Order Info
                  </Typography>
                </Box>
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Order Ref'
                      secondary={orderDetail.orderRef}
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                      secondaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Status'
                      secondary={
                        <Chip
                          label={orderDetail.orderStatus}
                          size='small'
                          color={getStatusColor(orderDetail.orderStatus)}
                        />
                      }
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Time Placed'
                      secondary={orderDetail.timePlaced}
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Time Wanted'
                      secondary={orderDetail.timeWanted}
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Delivery Method'
                      secondary={
                        <Chip
                          label={orderDetail.deliveryMethod}
                          size='small'
                          color={getDeliveryMethodColor(
                            orderDetail.deliveryMethod
                          )}
                          variant='outlined'
                        />
                      }
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary='Store'
                      secondary={`${orderDetail.storeNumber} - ${orderDetail.locationName}`}
                      primaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                      secondaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  {orderDetail.couponCode && (
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary='Coupon Code'
                        secondary={
                          <Box>
                            <Chip
                              label={orderDetail.couponCode}
                              size='small'
                              color='success'
                              variant='outlined'
                            />
                            {orderDetail.couponDescription && (
                              <Typography
                                variant='caption'
                                display='block'
                                color='text.secondary'
                                sx={{ mt: 0.5 }}
                              >
                                {orderDetail.couponDescription}
                              </Typography>
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>

              {/* Customer Info */}
              {orderDetail.customer && (
                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <PersonIcon color='primary' fontSize='small' />
                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                      Customer
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    <ListItem disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary='Name'
                        secondary={`${orderDetail.customer.firstName} ${orderDetail.customer.lastName}`}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: 'text.secondary',
                        }}
                        secondaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    {orderDetail.customer.phone && (
                      <ListItem disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary='Phone'
                          secondary={orderDetail.customer.phone}
                          primaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                    {orderDetail.customer.email && (
                      <ListItem disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary='Email'
                          secondary={orderDetail.customer.email}
                          primaryTypographyProps={{
                            variant: 'caption',
                            color: 'text.secondary',
                          }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}

              {/* Delivery Address */}
              {orderDetail.address && (
                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <LocalShippingIcon color='primary' fontSize='small' />
                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                      Delivery Address
                    </Typography>
                  </Box>
                  <Typography variant='body2'>
                    {orderDetail.address.streetAddress1}
                    {orderDetail.address.streetAddress2 && (
                      <>, {orderDetail.address.streetAddress2}</>
                    )}
                  </Typography>
                  <Typography variant='body2'>
                    {orderDetail.address.city}, {orderDetail.address.postalCode}
                  </Typography>
                  {orderDetail.address.comments && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mt: 1, fontStyle: 'italic' }}
                    >
                      {orderDetail.address.comments}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Items */}
              {orderDetail.items && orderDetail.items.length > 0 && (
                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <FastfoodIcon color='primary' fontSize='small' />
                    <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                      Items ({orderDetail.items.length})
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    {orderDetail.items.map((item, idx) => (
                      <React.Fragment key={idx}>
                        <ListItem disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography variant='body2'>
                                  {item.quantity}x {item.description}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  sx={{ fontWeight: 500 }}
                                >
                                  {formatCurrency(
                                    item.unitPrice * item.quantity
                                  )}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              item.modifiers &&
                              item.modifiers.length > 0 && (
                                <Box sx={{ pl: 2 }}>
                                  {item.modifiers.map((mod, midx) => (
                                    <Typography
                                      key={midx}
                                      variant='caption'
                                      display='block'
                                      color='text.secondary'
                                    >
                                      + {mod.description}
                                      {mod.unitPrice > 0 &&
                                        ` (${formatCurrency(mod.unitPrice)})`}
                                    </Typography>
                                  ))}
                                </Box>
                              )
                            }
                          />
                        </ListItem>
                        {item.specialInstructions && (
                          <Typography
                            variant='caption'
                            color='warning.main'
                            sx={{ pl: 2 }}
                          >
                            Note: {item.specialInstructions}
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Order Totals */}
              <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <PaymentIcon color='primary' fontSize='small' />
                  <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                    Order Totals
                  </Typography>
                </Box>
                <List dense disablePadding>
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary='Subtotal'
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    <Typography variant='body2'>
                      {formatCurrency(orderDetail.subtotal)}
                    </Typography>
                  </ListItem>
                  {orderDetail.discount > 0 && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary='Discount'
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: 'success.main',
                        }}
                      />
                      <Typography variant='body2' color='success.main'>
                        -{formatCurrency(orderDetail.discount)}
                      </Typography>
                    </ListItem>
                  )}
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary='Tax'
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    <Typography variant='body2'>
                      {formatCurrency(orderDetail.salesTax)}
                    </Typography>
                  </ListItem>
                  {orderDetail.deliveryFee > 0 && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary='Delivery Fee'
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Typography variant='body2'>
                        {formatCurrency(orderDetail.deliveryFee)}
                      </Typography>
                    </ListItem>
                  )}
                  {orderDetail.tip > 0 && (
                    <ListItem disablePadding sx={{ py: 0.25 }}>
                      <ListItemText
                        primary='Tip'
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Typography variant='body2'>
                        {formatCurrency(orderDetail.tip)}
                      </Typography>
                    </ListItem>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <ListItem disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary='Total'
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {formatCurrency(orderDetail.total)}
                    </Typography>
                  </ListItem>
                </List>
              </Paper>

              {/* Payments */}
              {orderDetail.payments && orderDetail.payments.length > 0 && (
                <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Payments
                  </Typography>
                  <List dense disablePadding>
                    {orderDetail.payments.map((payment, idx) => (
                      <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={payment.paymentType}
                          secondary={
                            payment.panSuffix
                              ? `****${payment.panSuffix}`
                              : payment.description
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Typography variant='body2'>
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Adjustments */}
              {orderDetail.adjustments &&
                orderDetail.adjustments.length > 0 && (
                  <Paper
                    variant='outlined'
                    sx={{ p: 2, backgroundColor: 'warning.50' }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Adjustments
                    </Typography>
                    <List dense disablePadding>
                      {orderDetail.adjustments.map((adj, idx) => (
                        <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={adj.adjustmentType}
                            secondary={`${adj.timeAdjusted}${
                              adj.adjustmentReason
                                ? ` - ${adj.adjustmentReason}`
                                : ''
                            }`}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                          <Typography variant='body2' color='error'>
                            {formatCurrency(adj.adjustmentAmount)}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
            </Box>
          ) : (
            <Typography color='text.secondary'>
              Unable to load order details
            </Typography>
          )}
        </Box>
      </Drawer>

      {/* Order Receipt Dialog */}
      <OrderReceiptDialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        order={orderDetail}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
    </div>
  );
};

export default OLOCoreLookup;
